import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { LanguageContext } from '../hooks/useLanguage';

const content = {
  es: {
    label: 'Nuestro trabajo',
    title: 'Instalaciones que\ndemuestran su valor.',
    subtitle: 'Cada proyecto con métricas reales. Ahorro medido, no estimado.',
    viewCase: 'Ver caso completo',
    devices: 'Dispositivos',
    saving: 'Ahorro anual',
    payback: 'Retorno',
    years: 'años',
  },
  en: {
    label: 'Our work',
    title: 'Installations that\nprove their worth.',
    subtitle: 'Every project with real metrics. Measured savings, not estimates.',
    viewCase: 'View full case study',
    devices: 'Devices',
    saving: 'Annual saving',
    payback: 'Payback',
    years: 'yr',
  },
  fr: {
    label: 'Nos réalisations',
    title: 'Des installations\nqui font leurs preuves.',
    subtitle: 'Chaque projet avec des métriques réelles. Économies mesurées, pas estimées.',
    viewCase: 'Voir l\'étude complète',
    devices: 'Appareils',
    saving: 'Économie annuelle',
    payback: 'Retour',
    years: 'ans',
  },
};

const projects = [
  {
    slug: 'eixample',
    type: { es: 'Residencial · Apartamento', en: 'Residential · Apartment', fr: 'Résidentiel · Appartement' },
    title: 'Apartamento Eixample',
    location: 'Barcelona, 2025 · 85 m²',
    desc: {
      es: 'Sistema inteligente completo para un piso de 3 habitaciones en el corazón del Eixample. Iluminación, persianas y climatización coordinadas por un único bus KNX.',
      en: 'Complete smart electrical system for a 3-bedroom apartment in the heart of Eixample. Lighting, blinds, and climate coordinated by a single KNX bus.',
      fr: 'Système électrique intelligent complet pour un appartement de 3 chambres au cœur de l\'Eixample. Éclairage, stores et climatisation coordonnés par un bus KNX.',
    },
    tags: ['KNX', 'DALI', 'Motorised Blinds', 'Climate'],
    stats: { devices: '42', saving: '31%', payback: '4.2', bill: '€640' },
    scene: { es: 'Escena Tarde', en: 'Evening Scene', fr: 'Scène Soirée' },
    sceneData: [
      { key: { es: 'Iluminación', en: 'Lighting', fr: 'Éclairage' }, val: '60% · 2700K' },
      { key: { es: 'Persianas', en: 'Blinds', fr: 'Stores' }, val: { es: 'Cerradas', en: 'Closed', fr: 'Fermés' } },
      { key: { es: 'Clima', en: 'Climate', fr: 'Climat' }, val: '21°C · Auto' },
    ],
    accent: '#C8A96E',
  },
  {
    slug: 'vilamoura',
    type: { es: 'Residencial · Villa', en: 'Residential · Villa', fr: 'Résidentiel · Villa' },
    title: 'Villa Vilamoura',
    location: 'Algarve, Portugal · 2025 · 160 m² · 3 plantas',
    desc: {
      es: 'Sistema para una villa de 3 plantas con protección solar automática. La gestión de persianas, climatización y presencia elimina el despilfarro energético típico de una propiedad de vacaciones.',
      en: 'System for a 3-floor villa with automatic solar management. Blind, climate, and presence logic eliminates the energy waste typical of a holiday property.',
      fr: 'Système pour une villa de 3 niveaux avec gestion solaire automatique. La logique stores, climatisation et présence élimine le gaspillage énergétique d\'une résidence de vacances.',
    },
    tags: ['KNX', 'DALI', 'Solar Management', '3 Floors'],
    stats: { devices: '38', saving: '34%', payback: '3.8', bill: '€820' },
    scene: { es: 'Tarde Piscina', en: 'Pool Evening', fr: 'Soirée Piscine' },
    sceneData: [
      { key: { es: 'Terraza', en: 'Terrace', fr: 'Terrasse' }, val: { es: 'Acento 40%', en: 'Accent 40%', fr: 'Accent 40%' } },
      { key: { es: 'Piscina', en: 'Pool lights', fr: 'Piscine' }, val: 'On · 60%' },
      { key: { es: 'Planta baja', en: 'Ground floor', fr: 'RDC' }, val: { es: 'Activa', en: 'Active', fr: 'Actif' } },
    ],
    accent: '#7BA7FF',
  },
];

const RoomScene: React.FC<{ slug: string }> = ({ slug }) => {
  if (slug === 'eixample') {
    return (
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
        {/* Wall */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: '35%', background: 'linear-gradient(180deg, #111010 0%, #161412 100%)' }} />
        {/* Floor */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '35%', background: 'linear-gradient(180deg, #1a1410 0%, #0f0d0a 100%)' }} />
        {/* Ceiling glow */}
        <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: 200, height: 200, background: 'radial-gradient(ellipse at 50% 0%, rgba(200,169,110,0.2) 0%, transparent 70%)', pointerEvents: 'none' }} />
        {/* Ambient */}
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 60% 40%, rgba(200,169,110,0.05) 0%, transparent 60%)', pointerEvents: 'none' }} />
        {/* Window */}
        <div style={{ position: 'absolute', top: '14%', left: '14%', width: 88, height: 110, border: '1px solid #2a2218', background: 'linear-gradient(135deg, #0d1520 0%, #0a1018 100%)' }}>
          <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 1, background: '#2a2218' }} />
          <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, background: '#2a2218' }} />
        </div>
        {/* Sofa */}
        <div style={{ position: 'absolute', bottom: '34%', left: '50%', transform: 'translateX(-50%)', width: 200 }}>
          <div style={{ width: 200, height: 32, background: '#1e1a16', borderTop: '2px solid #2a2420', borderRadius: '3px 3px 0 0' }} />
          <div style={{ width: 200, height: 20, background: '#191510', borderTop: '1px solid #252018' }} />
        </div>
        {/* Coffee table */}
        <div style={{ position: 'absolute', bottom: '30%', left: '50%', transform: 'translateX(-50%)', width: 72, height: 9, background: '#161210', borderTop: '1px solid #2a2218' }} />
        {/* Floor lamp glow */}
        <div style={{ position: 'absolute', bottom: '42%', right: '16%', width: 70, height: 70, background: 'radial-gradient(ellipse at 50% 50%, rgba(200,169,110,0.25) 0%, transparent 70%)', pointerEvents: 'none' }} />
      </div>
    );
  }
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      {/* Night sky */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '55%', background: 'linear-gradient(180deg, #0a0d12 0%, #0e1420 40%, #121820 100%)' }} />
      {/* Ground */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '45%', background: 'linear-gradient(180deg, #0e0c0a 0%, #0a0908 100%)' }} />
      {/* Moon */}
      <div style={{ position: 'absolute', top: '10%', right: '18%', width: 22, height: 22, borderRadius: '50%', background: 'radial-gradient(circle at 40% 40%, #f5e8c8 0%, #c8a96e 100%)', boxShadow: '0 0 20px rgba(200,169,110,0.35)' }} />
      {/* Pool */}
      <div style={{ position: 'absolute', bottom: '20%', left: '15%', right: '15%', height: '16%', background: 'linear-gradient(135deg, #0a1a28 0%, #0d2030 100%)', border: '1px solid rgba(100,160,220,0.18)' }} />
      {/* Pool glow */}
      <div style={{ position: 'absolute', bottom: '34%', left: '50%', transform: 'translateX(-50%)', width: 240, height: 60, background: 'radial-gradient(ellipse at 50% 100%, rgba(80,140,200,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
      {/* Deck */}
      <div style={{ position: 'absolute', bottom: '36%', left: 0, right: 0, height: '7%', background: '#14100c', borderTop: '1px solid rgba(200,169,110,0.1)' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, transparent 5%, rgba(200,169,110,0.3) 30%, rgba(200,169,110,0.3) 70%, transparent 95%)' }} />
      </div>
      {/* Villa windows */}
      <div style={{ position: 'absolute', top: '36%', left: '18%', width: 48, height: 32, background: 'rgba(200,160,80,0.18)', border: '1px solid rgba(200,160,80,0.25)', boxShadow: '0 0 16px rgba(200,160,80,0.08)' }} />
      <div style={{ position: 'absolute', top: '36%', left: '42%', width: 48, height: 32, background: 'rgba(200,160,80,0.18)', border: '1px solid rgba(200,160,80,0.25)', boxShadow: '0 0 16px rgba(200,160,80,0.08)' }} />
      <div style={{ position: 'absolute', top: '36%', left: '65%', width: 34, height: 32, background: 'rgba(20,20,20,0.6)', border: '1px solid rgba(255,255,255,0.04)' }} />
      <div style={{ position: 'absolute', top: '20%', left: '22%', width: 40, height: 28, background: 'rgba(200,160,80,0.2)', border: '1px solid rgba(200,160,80,0.28)', boxShadow: '0 0 14px rgba(200,160,80,0.08)' }} />
      <div style={{ position: 'absolute', top: '20%', left: '55%', width: 40, height: 28, background: 'rgba(20,20,20,0.6)', border: '1px solid rgba(255,255,255,0.04)' }} />
    </div>
  );
};

const Projects: React.FC = () => {
  const { lang } = useContext(LanguageContext);
  const t = content[lang];

  return (
    <section id="projects" style={{ background: '#0D0D0D', padding: 'clamp(60px, 8vw, 120px) 0' }}>
      <div className="container">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{ marginBottom: 64 }}
        >
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, letterSpacing: 5, textTransform: 'uppercase', color: '#C8A96E', display: 'block', marginBottom: 12 }}>
            {t.label}
          </span>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(28px, 4vw, 52px)', fontWeight: 700, color: '#FFFFFF', lineHeight: 1.1, letterSpacing: '-0.5px', whiteSpace: 'pre-line', marginBottom: 16 }}>
            {t.title}
          </h2>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 15, color: '#666', maxWidth: 480 }}>
            {t.subtitle}
          </p>
        </motion.div>

        {/* Project cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(480px, 1fr))', gap: 2 }} className="projects-grid">
          {projects.map((project, i) => (
            <motion.div
              key={project.slug}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.15 }}
              style={{ background: '#1A1A1A', border: '1px solid rgba(200,169,110,0.15)', display: 'flex', flexDirection: 'column' }}
            >
              {/* Visual panel */}
              <div style={{ position: 'relative', height: 280, overflow: 'hidden' }}>
                <RoomScene slug={project.slug} />

                {/* Scene HUD overlay */}
                <div style={{ position: 'absolute', top: 20, right: 20, background: 'rgba(13,13,13,0.88)', border: '1px solid rgba(200,169,110,0.2)', backdropFilter: 'blur(10px)', padding: '14px 18px', minWidth: 170 }}>
                  <div style={{ fontFamily: 'var(--font-body)', fontSize: 9, letterSpacing: 3, textTransform: 'uppercase', color: '#666', marginBottom: 6 }}>
                    {typeof project.scene === 'object' ? project.scene[lang] : project.scene}
                  </div>
                  {project.sceneData.map((row, j) => (
                    <div key={j} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontFamily: 'var(--font-body)', fontSize: 10, color: '#666', padding: '3px 0', borderBottom: j < project.sceneData.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                      <span>{typeof row.key === 'object' ? row.key[lang] : row.key}</span>
                      <span style={{ color: '#cccccc' }}>{typeof row.val === 'object' ? row.val[lang] : row.val}</span>
                    </div>
                  ))}
                </div>

                {/* Saving badge */}
                <div style={{ position: 'absolute', bottom: 20, left: 20, background: 'rgba(13,13,13,0.9)', border: '1px solid rgba(110,201,138,0.3)', padding: '12px 16px' }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: '#6EC98A', lineHeight: 1 }}>{project.stats.saving}</div>
                  <div style={{ fontFamily: 'var(--font-body)', fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: '#666', marginTop: 4 }}>{t.saving}</div>
                </div>

                {/* Type badge */}
                <div style={{ position: 'absolute', top: 20, left: 20, fontFamily: 'var(--font-body)', fontSize: 9, letterSpacing: 3, textTransform: 'uppercase', color: '#C8A96E', background: 'rgba(13,13,13,0.8)', border: '1px solid rgba(200,169,110,0.2)', padding: '5px 12px' }}>
                  {typeof project.type === 'object' ? project.type[lang] : project.type}
                </div>
              </div>

              {/* Content */}
              <div style={{ padding: 36, flex: 1, display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(22px, 2.5vw, 30px)', fontWeight: 700, color: '#FFFFFF', lineHeight: 1.1, marginBottom: 6 }}>
                    {project.title}
                  </h3>
                  <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: '#666' }}>
                    {project.location}
                  </div>
                </div>

                <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: '#888', lineHeight: 1.8 }}>
                  {project.desc[lang]}
                </p>

                {/* Stats row */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 2 }}>
                  {[
                    { label: t.devices, value: project.stats.devices },
                    { label: t.saving, value: project.stats.saving },
                    { label: t.payback, value: `${project.stats.payback} ${t.years}` },
                  ].map((s) => (
                    <div key={s.label} style={{ background: '#0D0D0D', border: '1px solid rgba(255,255,255,0.05)', padding: '14px 16px' }}>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: '#FFFFFF', lineHeight: 1 }}>{s.value}</div>
                      <div style={{ fontFamily: 'var(--font-body)', fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: '#666', marginTop: 4 }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Tags */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {project.tags.map((tag) => (
                    <span key={tag} style={{ fontFamily: 'var(--font-body)', fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: '#C8A96E', border: '1px solid rgba(200,169,110,0.2)', background: 'rgba(200,169,110,0.06)', padding: '4px 12px' }}>
                      {tag}
                    </span>
                  ))}
                </div>

                {/* CTA */}
                <motion.a
                  href={`/projects/${project.slug}.html`}
                  target="_blank"
                  rel="noreferrer"
                  whileHover={{ background: '#C8A96E', color: '#0D0D0D' }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: '#C8A96E', border: '1px solid #C8A96E', padding: '14px 24px', textDecoration: 'none', transition: 'all 0.2s', marginTop: 'auto', width: 'fit-content' }}
                >
                  {t.viewCase}
                  <span style={{ fontSize: 14 }}>→</span>
                </motion.a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 680px) {
          .projects-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
};

export default Projects;
